import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { beautyCreateBlockApi } from "../../../api/organizerBeautyApi";
import { SingleDatePickerPill } from "../../../pages/reservas/components/ReservaShared";
import OrganizerTimePillSelect from "../../components/OrganizerTimePillSelect";
import { Campo } from "../../reservas/primerUso/barPasos";
import { DATE_FORMAT, mensajeDe, toHHmm, toMin } from "./agendaLayout";
import "../../events/EventForm.css";
import "../../primerUso/primerUso.css";
import "../../reservas/reservas-organizer.css";
import "../../components/EventFormPickers.css";
import "./agenda.css";

/* BloqueoModal — "Bloquear horario": un profesional o todo el salón, un día entero o un rango (POST /blocks). */

const BloqueoModal = ({ salon, profesionales = [], inicial = {}, esEmpleado = false, onClose, onCreated }) => {
  const activos = useMemo(() => profesionales.filter((p) => p.active !== false), [profesionales]);
  // Una persona con acceso solo a su agenda no puede bloquear el salón: arranca en su propia columna.
  const porDefecto = inicial.professionalId || (esEmpleado ? activos[0]?.id || profesionales[0]?.id || "" : "");

  const [professionalId, setProfessionalId] = useState(porDefecto);
  const [date, setDate] = useState(inicial.date || dayjs().format(DATE_FORMAT));
  const [todoElDia, setTodoElDia] = useState(inicial.slotStart == null);
  const [desde, setDesde] = useState(inicial.slotStart || "09:00");
  const [hasta, setHasta] = useState(
    inicial.slotStart ? toHHmm(Math.min((toMin(inicial.slotStart) ?? 0) + 60, 1439)) : "10:00"
  );
  const [reason, setReason] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const minDate = useMemo(() => dayjs().startOf("day"), []);
  const maxDate = useMemo(() => minDate.add(365, "day"), [minDate]);

  const guardar = async () => {
    if (!todoElDia) {
      const a = toMin(desde);
      const b = toMin(hasta);
      if (a == null || b == null) {
        setError("Elegí desde y hasta qué hora");
        return;
      }
      if (b <= a) {
        setError("La hora de fin tiene que ser posterior a la de inicio");
        return;
      }
    }
    setError("");
    setGuardando(true);
    try {
      await beautyCreateBlockApi({
        barId: salon._id,
        date,
        ...(professionalId ? { professionalId } : {}),
        ...(todoElDia ? {} : { slotStart: desde, slotEnd: hasta }),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      await onCreated?.();
    } catch (e) {
      setError(mensajeDe(e, "No pudimos crear el bloqueo. Probá de nuevo."));
      setGuardando(false);
    }
  };

  return (
    <div className="zr-overlay" onMouseDown={guardando ? undefined : onClose}>
      <div className="zr-modal zr-side-panel zr-srv-modal ba-modal" role="dialog" aria-modal="true" aria-label="Bloquear horario" onMouseDown={(e) => e.stopPropagation()}>
        <div className="zr-side-panel__head">
          <h3 className="zr-side-panel__title">Bloquear horario</h3>
          <button type="button" className="zr-btn zr-btn--ghost zr-side-panel__close" onClick={onClose} aria-label="Cerrar" disabled={guardando}>
            ×
          </button>
        </div>
        <div className="pu-body">
          {error && <div className="ef-alert" role="alert">{error}</div>}
          <div className="pu-grid-2">
            <Campo label="Profesional">
              <select className="ef-input" aria-label="Profesional" value={professionalId} onChange={(e) => setProfessionalId(e.target.value)}>
                {!esEmpleado && <option value="">Todo el salón</option>}
                {profesionales.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Campo>
            <Campo label="Fecha">
              <SingleDatePickerPill
                value={dayjs(date)}
                onChange={(d) => d && d.isValid() && setDate(d.format(DATE_FORMAT))}
                minDate={minDate}
                maxDate={maxDate}
                ariaLabel="Fecha"
              />
            </Campo>
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" className="zr-switch" aria-label="Todo el día" checked={todoElDia} onChange={(e) => setTodoElDia(e.target.checked)} />
            Todo el día
          </label>
          {!todoElDia && (
            <div className="pu-grid-2">
              <Campo label="Desde">
                <OrganizerTimePillSelect triggerClassName="zr-day-row__time" ariaLabel="Desde" value={desde} onChange={setDesde} quarterHours />
              </Campo>
              <Campo label="Hasta">
                <OrganizerTimePillSelect triggerClassName="zr-day-row__time" ariaLabel="Hasta" value={hasta} onChange={setHasta} quarterHours />
              </Campo>
            </div>
          )}
          <Campo label="Motivo (opcional)">
            <input className="ef-input" aria-label="Motivo" placeholder="Ej: Capacitación" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Campo>
        </div>
        <div className="zr-side-panel__foot">
          <button type="button" className="zr-btn zr-btn--ghost" onClick={onClose} disabled={guardando}>Cancelar</button>
          <button type="button" className="zr-btn zr-btn--dark" onClick={guardar} disabled={guardando}>
            {guardando ? "Guardando..." : "Bloquear"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BloqueoModal;
